/**
 * AuthModal Component
 * Modal for login and signup forms
 */

import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { HiX, HiMail, HiLockClosed, HiUser, HiEye, HiEyeOff, HiArrowLeft, HiPhone } from 'react-icons/hi'
import { FcGoogle } from 'react-icons/fc'
import { FaDiscord } from 'react-icons/fa'
import useAuth from '../hooks/useAuth'
import logger from '../utils/logger'
import { FEATURE_FLAGS } from '../utils/featureFlags'
import '../styles/animations.css'; // Ensure animations are available
import './AuthModal.css';

/**
 * Password strength calculator
 */
function getPasswordStrength(password) {
    if (!password) return { score: 0, label: '', color: '' }

    let score = 0

    // Length checks
    if (password.length >= 8) score += 1
    if (password.length >= 12) score += 1

    // Character variety checks
    if (/[a-z]/.test(password)) score += 1
    if (/[A-Z]/.test(password)) score += 1
    if (/[0-9]/.test(password)) score += 1
    if (/[^a-zA-Z0-9]/.test(password)) score += 1

    // Normalize to 0-4 scale
    const normalizedScore = Math.min(4, Math.floor(score * 4 / 6))

    const labels = ['Weak', 'Fair', 'Good', 'Strong']
    const colors = ['var(--error-color)', 'var(--warning-color)', 'var(--accent-secondary)', 'var(--success-color)']

    return {
        score: normalizedScore,
        label: labels[normalizedScore - 1] || '',
        color: colors[normalizedScore - 1] || ''
    }
}

/**
 * AuthModal component
 */
export default function AuthModal({ isOpen, onClose, initialTab = 'login', onLoginSuccess }) {
    const { signIn, signUp, resetPassword, signInWithMagicLink, signInWithProvider, signInWithPhone, verifyPhoneOtp, loading } = useAuth()

    const [activeTab, setActiveTab] = useState(initialTab)
    const [view, setView] = useState('auth') // 'auth' | 'forgotPassword' | 'magicLink' | 'phone' | 'phoneVerify'
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [displayName, setDisplayName] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [oauthLoading, setOauthLoading] = useState(null) // 'google' | 'discord' | null
    const [phoneNumber, setPhoneNumber] = useState('')
    const [otpCode, setOtpCode] = useState('')
    const [phoneLoading, setPhoneLoading] = useState(false)

    // Reset form when modal opens/closes or tab changes
    useEffect(() => {
        if (isOpen) {
            setActiveTab(initialTab)
            setView('auth')
        }
    }, [isOpen, initialTab])

    useEffect(() => {
        setError('')
        setSuccess('')
        setPassword('')
        setConfirmPassword('')
        setShowPassword(false)
    }, [activeTab, view])

    // Close on escape key
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose()
            }
        }
        window.addEventListener('keydown', handleEscape)
        return () => window.removeEventListener('keydown', handleEscape)
    }, [isOpen, onClose])

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return re.test(email)
    }

    // OAuth handler
    const handleOAuthSignIn = useCallback(async (provider) => {
        setError('')
        setOauthLoading(provider)

        try {
            const { error } = await signInWithProvider(provider)
            if (error) {
                setError(error.message || `Failed to sign in with ${provider}`)
                logger.warn('OAuth sign in failed', { provider, error: error.message })
            }
            // Note: OAuth redirects away from the page, so success is handled on return
        } catch (err) {
            setError('An unexpected error occurred')
            logger.error('OAuth error', { provider, error: err.message })
        } finally {
            setOauthLoading(null)
        }
    }, [signInWithProvider])

    const handleForgotPassword = useCallback(async (e) => {
        e.preventDefault()
        setError('')
        setSuccess('')

        if (!email) {
            setError('Please enter your email address')
            return
        }

        if (!validateEmail(email)) {
            setError('Please enter a valid email address')
            return
        }

        setIsSubmitting(true)

        try {
            const result = await resetPassword(email)

            if (result.error) {
                setError(result.error.message || 'Failed to send reset email')
                return
            }

            setSuccess('Password reset email sent! Check your inbox.')
            logger.info('Password reset email requested', { email })
        } catch (err) {
            setError('An unexpected error occurred')
            logger.error('Forgot password error', { error: err.message })
        } finally {
            setIsSubmitting(false)
        }
    }, [email, resetPassword])

    const handleMagicLink = useCallback(async (e) => {
        e.preventDefault()
        setError('')
        setSuccess('')

        if (!email) {
            setError('Please enter your email address')
            return
        }

        if (!validateEmail(email)) {
            setError('Please enter a valid email address')
            return
        }

        setIsSubmitting(true)

        try {
            const result = await signInWithMagicLink(email)

            if (result.error) {
                setError(result.error.message || 'Failed to send magic link')
                return
            }

            setSuccess('Magic link sent! Check your email to sign in.')
            logger.info('Magic link email requested', { email })
        } catch (err) {
            setError('An unexpected error occurred')
            logger.error('Magic link error', { error: err.message })
        } finally {
            setIsSubmitting(false)
        }
    }, [email, signInWithMagicLink])

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault()
        setError('')
        setSuccess('')

        // Validation
        if (!email || !password) {
            setError('Please fill in all fields')
            return
        }

        if (!validateEmail(email)) {
            setError('Please enter a valid email address')
            return
        }

        if (password.length < 6) {
            setError('Password must be at least 6 characters')
            return
        }

        if (activeTab === 'signup') {
            if (password !== confirmPassword) {
                setError('Passwords do not match')
                return
            }
        }

        setIsSubmitting(true)

        try {
            if (activeTab === 'login') {
                const { error } = await signIn(email, password)

                if (error) {
                    setError(error.message || 'Failed to sign in')
                    return
                }

                logger.info('User logged in', { email })
                onClose()
            } else {
                const { error, data } = await signUp(email, password, displayName || null)

                if (error) {
                    setError(error.message || 'Failed to sign up')
                    return
                }

                // Check if this is an existing user (Supabase returns fake user with empty identities)
                if (data?.user && (!data.user.identities || data.user.identities.length === 0)) {
                    setError('An account with this email already exists. Please sign in instead.')
                    logger.info('Duplicate sign-up attempt detected', { email })
                    return
                }

                // Check if email confirmation is required (genuine new user)
                if (data?.user && !data.session) {
                    setSuccess('Account created! Please check your email to verify your account.')
                } else {
                    logger.info('User signed up', { email })
                    onClose()
                }
            }
        } catch (err) {
            setError('An unexpected error occurred')
            logger.error('Auth error', { error: err.message })
        } finally {
            setIsSubmitting(false)
        }
    }, [email, password, confirmPassword, displayName, activeTab, signIn, signUp, onClose])

    const handleSendOtp = useCallback(async (e) => {
        e.preventDefault()
        setError('')
        setSuccess('')

        if (!phoneNumber) {
            setError('Please enter your phone number')
            return
        }

        // Basic phone validation (allowing international format)
        if (phoneNumber.length < 8) {
            setError('Please enter a valid phone number')
            return
        }

        setPhoneLoading(true)

        try {
            const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`
            const result = await signInWithPhone(formattedPhone)

            if (result.error) {
                setError(result.error.message || 'Failed to send verification code')
                return
            }

            setView('phoneVerify')
            setSuccess('Verification code sent!')
        } catch (err) {
            setError('An unexpected error occurred')
            logger.error('Phone auth error', { error: err.message })
        } finally {
            setPhoneLoading(false)
        }
    }, [phoneNumber, signInWithPhone])

    const handleVerifyOtp = useCallback(async (e) => {
        e.preventDefault()
        setError('')
        setSuccess('')

        if (!otpCode || otpCode.length !== 6) {
            setError('Please enter the 6-digit code')
            return
        }

        setPhoneLoading(true)

        try {
            const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`
            const result = await verifyPhoneOtp(formattedPhone, otpCode)

            if (result.error) {
                setError(result.error.message || 'Invalid verification code')
                return
            }

            logger.info('Phone login successful')
            onClose()
        } catch (err) {
            setError('An unexpected error occurred')
            logger.error('Phone verification error', { error: err.message })
        } finally {
            setPhoneLoading(false)
        }
    }, [phoneNumber, otpCode, verifyPhoneOtp, onClose])

    const passwordStrength = getPasswordStrength(password)

    if (!isOpen) return null

    // Forgot Password View
    if (view === 'forgotPassword') {
        return (
            <AnimatePresence>
                <motion.div
                    className="auth-modal-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="auth-modal"
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <button
                            className="auth-modal-close"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <HiX />
                        </button>

                        {/* Header */}
                        <div className="auth-modal-header">
                            <button
                                type="button"
                                onClick={() => setView('auth')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 0',
                                    marginBottom: '0.5rem'
                                }}
                            >
                                <HiArrowLeft /> Back to login
                            </button>
                            <h2 className="auth-modal-title gradient-text">
                                Reset Password
                            </h2>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                                Enter your email and we&apos;ll send you a reset link.
                            </p>
                        </div>

                        {/* Form */}
                        <form className="auth-form" onSubmit={handleForgotPassword}>
                            {/* Error/Success messages */}
                            {error && (
                                <motion.div
                                    className="auth-message error"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    {error}
                                </motion.div>
                            )}
                            {success && (
                                <motion.div
                                    className="auth-message success"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    {success}
                                </motion.div>
                            )}

                            {/* Email */}
                            <div className="auth-field">
                                <label htmlFor="resetEmail" className="auth-label">
                                    <HiMail className="auth-icon" />
                                    Email
                                </label>
                                <input
                                    id="resetEmail"
                                    type="email"
                                    className="auth-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    required
                                    autoComplete="email"
                                />
                            </div>

                            {/* Submit button */}
                            <button
                                type="submit"
                                className="btn btn-primary auth-submit"
                                disabled={isSubmitting || loading || success}
                            >
                                {isSubmitting ? (
                                    <span className="auth-loading">
                                        <span className="auth-loading-dot" />
                                        <span className="auth-loading-dot" />
                                        <span className="auth-loading-dot" />
                                    </span>
                                ) : success ? (
                                    'Email Sent!'
                                ) : (
                                    'Send Reset Link'
                                )}
                            </button>
                        </form>
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        )
    }

    // Magic Link View
    if (view === 'magicLink') {
        return (
            <AnimatePresence>
                <motion.div
                    className="auth-modal-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="auth-modal"
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <button
                            className="auth-modal-close"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <HiX />
                        </button>

                        {/* Header */}
                        <div className="auth-modal-header">
                            <button
                                type="button"
                                onClick={() => setView('auth')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 0',
                                    marginBottom: '0.5rem'
                                }}
                            >
                                <HiArrowLeft /> Back to login
                            </button>
                            <h2 className="auth-modal-title gradient-text">
                                Sign in with Email
                            </h2>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                                We&apos;ll send you a magic link - no password needed!
                            </p>
                        </div>

                        {/* Form */}
                        <form className="auth-form" onSubmit={handleMagicLink}>
                            {/* Error/Success messages */}
                            {error && (
                                <motion.div
                                    className="auth-message error"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    {error}
                                </motion.div>
                            )}
                            {success && (
                                <motion.div
                                    className="auth-message success"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    {success}
                                </motion.div>
                            )}

                            {/* Email */}
                            <div className="auth-field">
                                <label htmlFor="magicLinkEmail" className="auth-label">
                                    <HiMail className="auth-icon" />
                                    Email
                                </label>
                                <input
                                    id="magicLinkEmail"
                                    type="email"
                                    className="auth-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    required
                                    autoComplete="email"
                                />
                            </div>

                            {/* Submit button */}
                            <button
                                type="submit"
                                className="btn btn-primary auth-submit"
                                disabled={isSubmitting || loading || success}
                            >
                                {isSubmitting ? (
                                    <span className="auth-loading">
                                        <span className="auth-loading-dot" />
                                        <span className="auth-loading-dot" />
                                        <span className="auth-loading-dot" />
                                    </span>
                                ) : success ? (
                                    'Email Sent!'
                                ) : (
                                    'Send Magic Link'
                                )}
                            </button>
                        </form>
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        )
    }

    // Phone Input View
    if (view === 'phone') {
        return (
            <AnimatePresence>
                <motion.div
                    className="auth-modal-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="auth-modal"
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <button className="auth-modal-close" onClick={onClose} aria-label="Close">
                            <HiX />
                        </button>

                        {/* Header */}
                        <div className="auth-modal-header">
                            <button
                                type="button"
                                onClick={() => setView('auth')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 0',
                                    marginBottom: '0.5rem'
                                }}
                            >
                                <HiArrowLeft /> Back to options
                            </button>
                            <h2 className="auth-modal-title gradient-text">Sign in with Phone</h2>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                                Enter your phone number to receive a verification code.
                            </p>
                        </div>

                        {/* Form */}
                        <form className="auth-form" onSubmit={handleSendOtp}>
                            {/* Error/Success messages */}
                            {error && (
                                <motion.div className="auth-message error" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                                    {error}
                                </motion.div>
                            )}

                            {/* Phone Input */}
                            <div className="auth-field">
                                <label htmlFor="phone" className="auth-label">
                                    <HiPhone className="auth-icon" />
                                    Phone Number
                                </label>
                                <div className="phone-input-wrapper">
                                    <input
                                        id="phone"
                                        type="tel"
                                        className="auth-input phone-number-input"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        placeholder="+1 234 567 8900"
                                        required
                                        autoComplete="tel"
                                    />
                                </div>
                                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                                    Include country code (e.g., +1 for US)
                                </p>
                            </div>

                            {/* Submit button */}
                            <button
                                type="submit"
                                className="btn btn-primary auth-submit"
                                disabled={phoneLoading || loading}
                            >
                                {phoneLoading ? (
                                    <span className="auth-loading">
                                        <span className="auth-loading-dot" />
                                        <span className="auth-loading-dot" />
                                        <span className="auth-loading-dot" />
                                    </span>
                                ) : (
                                    'Send Code'
                                )}
                            </button>
                        </form>
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        )
    }

    // OTP Verification View
    if (view === 'phoneVerify') {
        return (
            <AnimatePresence>
                <motion.div
                    className="auth-modal-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        className="auth-modal"
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close button */}
                        <button className="auth-modal-close" onClick={onClose} aria-label="Close">
                            <HiX />
                        </button>

                        {/* Header */}
                        <div className="auth-modal-header">
                            <button
                                type="button"
                                onClick={() => setView('phone')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    padding: '0.5rem 0',
                                    marginBottom: '0.5rem'
                                }}
                            >
                                <HiArrowLeft /> Change number
                            </button>
                            <h2 className="auth-modal-title gradient-text">Verify Code</h2>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                                Enter the 6-digit code sent to {phoneNumber}
                            </p>
                        </div>

                        {/* Form */}
                        <form className="auth-form" onSubmit={handleVerifyOtp}>
                            {/* Error/Success messages */}
                            {error && (
                                <motion.div className="auth-message error" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                                    {error}
                                </motion.div>
                            )}
                            {success && (
                                <motion.div className="auth-message success" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                                    {success}
                                </motion.div>
                            )}

                            {/* OTP Input */}
                            <div className="auth-field">
                                <label htmlFor="otp" className="auth-label" style={{ justifyContent: 'center' }}>
                                    Verification Code
                                </label>
                                <div className="otp-input">
                                    <input
                                        id="otp"
                                        type="text"
                                        value={otpCode}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, 6)
                                            setOtpCode(val)
                                        }}
                                        placeholder="123456"
                                        required
                                        maxLength={6}
                                        autoComplete="one-time-code"
                                    />
                                </div>
                            </div>

                            {/* Submit button */}
                            <button
                                type="submit"
                                className="btn btn-primary auth-submit"
                                disabled={phoneLoading || loading}
                            >
                                {phoneLoading ? (
                                    <span className="auth-loading">
                                        <span className="auth-loading-dot" />
                                        <span className="auth-loading-dot" />
                                        <span className="auth-loading-dot" />
                                    </span>
                                ) : (
                                    'Verify & Login'
                                )}
                            </button>

                            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                                <button
                                    type="button"
                                    onClick={handleSendOtp}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: 'var(--accent-secondary)',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem'
                                    }}
                                    disabled={phoneLoading}
                                >
                                    Resend Code
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            </AnimatePresence>
        )
    }

    return (
        <AnimatePresence>
            <motion.div
                className="auth-modal-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    className="auth-modal"
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close button */}
                    <button
                        className="auth-modal-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <HiX />
                    </button>

                    {/* Header */}
                    <div className="auth-modal-header">
                        <h2 className="auth-modal-title gradient-text">
                            {activeTab === 'login' ? 'Welcome Back' : 'Create Account'}
                        </h2>
                    </div>

                    {/* OAuth Buttons */}
                    <div className="oauth-buttons">
                        <button
                            type="button"
                            className="oauth-button google"
                            onClick={() => handleOAuthSignIn('google')}
                            disabled={oauthLoading || isSubmitting}
                        >
                            {oauthLoading === 'google' ? (
                                <span className="auth-loading">
                                    <span className="auth-loading-dot" />
                                    <span className="auth-loading-dot" />
                                    <span className="auth-loading-dot" />
                                </span>
                            ) : (
                                <>
                                    <FcGoogle />
                                    Continue with Google
                                </>
                            )}
                        </button>
                        <button
                            type="button"
                            className="oauth-button discord"
                            onClick={() => handleOAuthSignIn('discord')}
                            disabled={oauthLoading || isSubmitting}
                        >
                            {oauthLoading === 'discord' ? (
                                <span className="auth-loading">
                                    <span className="auth-loading-dot" />
                                    <span className="auth-loading-dot" />
                                    <span className="auth-loading-dot" />
                                </span>
                            ) : (
                                <>
                                    <FaDiscord style={{ color: '#5865F2' }} />
                                    Continue with Discord
                                </>
                            )}
                        </button>
                        {FEATURE_FLAGS.PHONE_AUTH && (
                            <button
                                type="button"
                                className="oauth-button phone"
                                onClick={() => setView('phone')}
                                disabled={oauthLoading || isSubmitting}
                            >
                                <HiPhone style={{ color: '#10B981' }} />
                                Continue with Phone
                            </button>
                        )}
                    </div>

                    <div className="oauth-divider">or continue with email</div>

                    {/* Tabs */}
                    <div className="auth-tabs">
                        <button
                            className={`auth-tab ${activeTab === 'login' ? 'active' : ''}`}
                            onClick={() => setActiveTab('login')}
                            type="button"
                        >
                            Login
                        </button>
                        <button
                            className={`auth-tab ${activeTab === 'signup' ? 'active' : ''}`}
                            onClick={() => setActiveTab('signup')}
                            type="button"
                        >
                            Sign Up
                        </button>
                    </div>

                    {/* Form */}
                    <form className="auth-form" onSubmit={handleSubmit}>
                        {/* Error/Success messages */}
                        {error && (
                            <motion.div
                                className="auth-message error"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                {error}
                            </motion.div>
                        )}
                        {success && (
                            <motion.div
                                className="auth-message success"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                {success}
                            </motion.div>
                        )}

                        {/* Display name (signup only) */}
                        {activeTab === 'signup' && (
                            <div className="auth-field">
                                <label htmlFor="displayName" className="auth-label">
                                    <HiUser className="auth-icon" />
                                    Display Name (optional)
                                </label>
                                <input
                                    id="displayName"
                                    type="text"
                                    className="auth-input"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    placeholder="How should we call you?"
                                    maxLength={100}
                                    autoComplete="name"
                                />
                            </div>
                        )}

                        {/* Email */}
                        <div className="auth-field">
                            <label htmlFor="email" className="auth-label">
                                <HiMail className="auth-icon" />
                                Email
                            </label>
                            <input
                                id="email"
                                type="email"
                                className="auth-input"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                required
                                autoComplete="email"
                            />
                        </div>

                        {/* Password */}
                        <div className="auth-field">
                            <label htmlFor="password" className="auth-label">
                                <HiLockClosed className="auth-icon" />
                                Password
                            </label>
                            <div className="auth-input-wrapper">
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    className="auth-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    minLength={6}
                                    autoComplete={activeTab === 'login' ? 'current-password' : 'new-password'}
                                />
                                <button
                                    type="button"
                                    className="auth-password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? <HiEyeOff /> : <HiEye />}
                                </button>
                            </div>

                            {/* Forgot password link (login only) */}
                            {activeTab === 'login' && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', paddingTop: '0.5rem' }}>
                                    <button
                                        type="button"
                                        onClick={() => setView('magicLink')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'var(--accent-secondary)',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem',
                                            padding: '0'
                                        }}
                                    >
                                        Sign in with email link
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setView('forgotPassword')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'var(--accent-primary)',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem',
                                            padding: '0'
                                        }}
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                            )}

                            {/* Password strength (signup only) */}
                            {activeTab === 'signup' && password && (
                                <div className="password-strength">
                                    <div className="password-strength-bar">
                                        {[1, 2, 3, 4].map((level) => (
                                            <div
                                                key={level}
                                                className={`password-strength-segment ${level <= passwordStrength.score ? 'active' : ''}`}
                                                style={{ backgroundColor: level <= passwordStrength.score ? passwordStrength.color : undefined }}
                                            />
                                        ))}
                                    </div>
                                    {passwordStrength.label && (
                                        <span className="password-strength-label" style={{ color: passwordStrength.color }}>
                                            {passwordStrength.label}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Confirm Password (signup only) */}
                        {activeTab === 'signup' && (
                            <div className="auth-field">
                                <label htmlFor="confirmPassword" className="auth-label">
                                    <HiLockClosed className="auth-icon" />
                                    Confirm Password
                                </label>
                                <input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    className="auth-input"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    minLength={6}
                                    autoComplete="new-password"
                                />
                                {confirmPassword && password !== confirmPassword && (
                                    <span className="auth-field-error">Passwords do not match</span>
                                )}
                            </div>
                        )}

                        {/* Submit button */}
                        <button
                            type="submit"
                            className="btn btn-primary auth-submit"
                            disabled={isSubmitting || loading}
                        >
                            {isSubmitting ? (
                                <span className="auth-loading">
                                    <span className="auth-loading-dot" />
                                    <span className="auth-loading-dot" />
                                    <span className="auth-loading-dot" />
                                </span>
                            ) : activeTab === 'login' ? (
                                'Login'
                            ) : (
                                'Create Account'
                            )}
                        </button>
                    </form>

                    {/* Footer */}
                    <div className="auth-modal-footer">
                        {activeTab === 'login' ? (
                            <p>
                                Don&apos;t have an account?{' '}
                                <button
                                    type="button"
                                    className="auth-link"
                                    onClick={() => setActiveTab('signup')}
                                >
                                    Sign up
                                </button>
                            </p>
                        ) : (
                            <p>
                                Already have an account?{' '}
                                <button
                                    type="button"
                                    className="auth-link"
                                    onClick={() => setActiveTab('login')}
                                >
                                    Login
                                </button>
                            </p>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    )
}
